import React from 'react'
import PropTypes from 'prop-types'
import { Input as ReactstrapInput } from 'reactstrap'
import omit from 'lodash/omit'

class Input extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      value: this.props.field.value,
    }

    this.initialState = this.state
  }

  resetForm = () => {
    const { type } = this.state
    if (type === 'number') {
      this.setState({ value: '' })
    } else {
      this.setState(this.initialState)
    }
  }

  componentWillReceiveProps (nextProps) {
    const { value } = this.state
    const {
      field: { value: nextValue },
    } = nextProps

    if (value === nextValue) {
      return
    }

    this.setState({
      value: nextValue,
    })
  }

  constrainInput = value =>
    this.constrainLength(this.constrainValue(this.constrainNumber(value)))

  notifyChangeHandlers = e => {
    const { handleChange: propHandleChange, willUseReset, field } = this.props
    const { onChange: fieldHandleChange } = field
    propHandleChange && !willUseReset && propHandleChange(e)
    fieldHandleChange && fieldHandleChange(e)
  }

  onChange = e => {
    e.persist()
    const {
      target: { value },
    } = e
    const { handleChange: propHandleChange, willUseReset } = this.props

    this.setState(
      {
        value: this.constrainInput(value),
      },
      () => {
        const { value: newValue } = this.state
        e.target.value = newValue
        this.notifyChangeHandlers(e)
      }
    )
    propHandleChange && willUseReset && propHandleChange(e, this.resetForm)
  }

  constrainValue = value => {
    const { type, inputMode, maxValue } = this.props
    const { value: stateValue } = this.state

    const shouldUseNumericCeiling =
      type === 'number' || (type === 'text' && (inputMode === 'decimal' || inputMode === 'numeric'))

    if (shouldUseNumericCeiling && maxValue && Number(value) > Number(maxValue)) {
      return stateValue
    }

    return value
  }

  constrainLength = value => {
    const { maxLength, type, inputMode, maxWholeDigits } = this.props
    const isDecimalTextInput = type === 'text' && inputMode === 'decimal'
    const isNumericInput = type === 'number' || isDecimalTextInput

    // For numeric inputs using whole-digit constraints, avoid char slicing.
    if (isNumericInput && maxWholeDigits !== undefined && maxWholeDigits !== null) {
      return value
    }

    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength)
    }

    return value
  }

  fractionPlacesFromStep = step => {
    const normalized = String(step).trim().toLowerCase()
    if (normalized === 'any') {
      return Number.POSITIVE_INFINITY
    }
    const dot = normalized.indexOf('.')
    if (dot === -1) {
      return 0
    }
    return normalized.length - dot - 1
  }

  applyDecimalConstraints = (value, step, allowNegative = false) => {
    const placesFromStep = this.fractionPlacesFromStep(step)

    // If decimals are allowed, always enforce exactly two decimal places max.
    if (placesFromStep > 0 || placesFromStep === Number.POSITIVE_INFINITY) {
      return this.limitDecimalPlaces(
        this.sanitizeNumeric(value, allowNegative),
        2
      )
    }

    return this.limitDecimalPlaces(this.sanitizeNumeric(value, allowNegative), 0)
  }

  enforceWholeDigitLimit = (value, maxWholeDigits) => {
    if (maxWholeDigits === undefined || maxWholeDigits === null) {
      return value
    }

    const parsedLimit = Number(maxWholeDigits)
    if (!Number.isFinite(parsedLimit) || parsedLimit < 0) {
      return value
    }

    const stringValue = String(value)
    const isNegative = stringValue.startsWith('-')
    const unsignedValue = isNegative ? stringValue.slice(1) : stringValue
    const [wholePart = '', decimalPart] = unsignedValue.split('.')
    const wholeDigitsOnly = wholePart.replace(/[^0-9]/g, '')

    if (wholeDigitsOnly.length <= parsedLimit) {
      return value
    }

    const trimmedWhole = wholeDigitsOnly.slice(0, parsedLimit)
    const withSign = `${isNegative ? '-' : ''}${trimmedWhole}`

    // Preserve the decimal section so fractional entry does not affect whole-digit limit.
    if (decimalPart !== undefined) {
      return `${withSign}.${decimalPart}`
    }

    return withSign
  }

  constrainNumber = value => {
    const { type, inputMode, step, maxWholeDigits, allowNegative } = this.props
    const isDecimalTextInput = type === 'text' && inputMode === 'decimal'
    const isWholeNumberTextInput = type === 'text' && inputMode === 'numeric'
    const isNumericInput =
      type === 'number' || isDecimalTextInput || isWholeNumberTextInput

    if (isNumericInput) {
      if (isDecimalTextInput && step !== undefined && step !== null && step !== '') {
        const decimalPlaces = this.fractionPlacesFromStep(step)
        value = this.normalizeDecimalInput(value, {
          allowNegative,
          maxWholeDigits,
          decimalPlaces,
        })
      } else if (step === undefined || step === null || step === '') {
        value = String(value).replace(/[^0-9]/g, '')
      } else {
        value = this.applyDecimalConstraints(value, step, allowNegative)
        value = this.enforceWholeDigitLimit(value, maxWholeDigits)
      }
    } else if (type === 'currency') {
      value = this.constrainWholeCurrency(value)
    } else if (type === 'negative-currency') {
      value = this.constrainNegativeCurrency(value)
    }

    return value
  }

  normalizeDecimalInput = (
    value,
    { allowNegative = false, maxWholeDigits, decimalPlaces = 2 }
  ) => {
    const parsedWholeLimit = Number(maxWholeDigits)
    const hasWholeLimit =
      Number.isFinite(parsedWholeLimit) && parsedWholeLimit >= 0
    const rawValue = String(value ?? '')

    const wantsNegative = allowNegative && rawValue.trim().startsWith('-')
    let sanitized = rawValue.replace(allowNegative ? /[^0-9.-]/g : /[^0-9.]/g, '')
    sanitized = sanitized.replace(/-/g, '')

    const hasTrailingDot = sanitized.endsWith('.')
    const [wholeRaw = '', ...decimalRawParts] = sanitized.split('.')
    const wholeDigits = hasWholeLimit ? wholeRaw.slice(0, parsedWholeLimit) : wholeRaw
    const decimalRaw = decimalRawParts.join('').replace(/\./g, '')

    let decimalDigits = decimalRaw
    if (decimalPlaces !== Number.POSITIVE_INFINITY) {
      decimalDigits = decimalRaw.slice(0, Math.max(decimalPlaces, 0))
    }

    const sign = wantsNegative ? '-' : ''
    const shouldKeepDot =
      hasTrailingDot || decimalRawParts.length > 0

    if (!shouldKeepDot) {
      return `${sign}${wholeDigits}`
    }

    return `${sign}${wholeDigits}.${decimalDigits}`
  }

  sanitizeNumeric = (value, allowNegative = false) => {
    let sanitized = String(value).replace(
      allowNegative ? /[^0-9.-]/g : /[^0-9.]/g,
      ''
    )

    if (allowNegative) {
      const isNegative = sanitized.startsWith('-')
      sanitized = sanitized.replace(/-/g, '')
      if (isNegative) {
        sanitized = `-${sanitized}`
      }
    }

    const firstDotIndex = sanitized.indexOf('.')

    if (firstDotIndex === -1) {
      return sanitized
    }

    const whole = sanitized.slice(0, firstDotIndex)
    const decimal = sanitized.slice(firstDotIndex + 1).replace(/\./g, '')
    return `${whole}.${decimal}`
  }

  limitDecimalPlaces = (value, decimalPlaces) => {
    if (decimalPlaces === Number.POSITIVE_INFINITY) {
      return value
    }

    if (decimalPlaces <= 0) {
      return String(value).replace(/[^0-9]/g, '')
    }

    const firstDotIndex = String(value).indexOf('.')
    if (firstDotIndex === -1) {
      return String(value)
    }

    const whole = String(value).slice(0, firstDotIndex)
    const decimal = String(value).slice(firstDotIndex + 1, firstDotIndex + 1 + decimalPlaces)
    return `${whole}.${decimal}`
  }

  constrainWholeCurrency = value => {
    return String(value).replace(/[^0-9.]/g, '')
  }

  constrainNegativeCurrency = value => {
    return String(value).replace(/[^-0-9.]/g, '')
  }

  render () {
    const {
      'data-test': dataTest,
      id,
      type,
      disabled,
      invalid,
      children,
      handleBlur,
      field,
      form,
      ...rest
    } = this.props
    const { name, onBlur } = field
    const { errors, touched } = form
    const { value } = this.state

    return (
      <ReactstrapInput
        {...omit(rest, [
          'willUseReset',
          'handleChange',
          'maxValue',
          'maxLength',
          'resetValue',
          'maxWholeDigits',
          'allowNegative',
        ])}
        disabled={disabled}
        data-test={dataTest || name}
        id={id || name}
        type={type}
        name={name}
        value={value}
        invalid={invalid || !!(touched[name] && errors[name])}
        onBlur={
          handleBlur
            ? e => {
              handleBlur(e)
              onBlur(e)
            }
            : onBlur
        }
        onChange={this.onChange}
      >
        {children}
      </ReactstrapInput>
    )
  }
}

Input.defaultProps = {
  disabled: false,
  type: 'text',
}

Input.propTypes = {
  children: PropTypes.node,
  'data-test': PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  inputMode: PropTypes.string,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
  maxLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWholeDigits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allowNegative: PropTypes.bool,
  maxValue: PropTypes.number,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  willUseReset: PropTypes.bool,
  resetValue: PropTypes.any,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
  }).isRequired,
}

export default Input
